import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import { io } from 'socket.io-client';
import { MessageSquare, Users, BarChart3, HelpCircle, Radio, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import useWebinarStore from '@/stores/webinarStore';
import VideoGrid from '@/components/webinars/VideoGrid';
import LiveChat from '@/components/webinars/LiveChat';
import MediaControls from '@/components/webinars/MediaControls';
import ParticipantsList from '@/components/webinars/ParticipantsList';
import LivePolls from '@/components/webinars/LivePolls';
import QnAPanel from '@/components/webinars/QnAPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WebinarLivePage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    webinar, setWebinar, setSocket, setParticipants, setMyRole,
    addMessage, setMessages, removeMessage, pinMessage,
    setRaisedHands, setScreenSharer, addReaction, clearReaction,
    setActivePoll, activePanel, setActivePanel, isPanelOpen, togglePanel,
    participants, messages, reactions, screenSharer, isMuted, isCameraOn,
    reset,
  } = useWebinarStore();

  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const [localStreamState, setLocalStreamState] = useState(null);
  const screenStreamRef = useRef(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const peerConnectionsRef = useRef({});

  const isHost = webinar?.created_by === user?.id || user?.role === 'admin';

  // Load webinar data
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await webinarAPI.get(webinarId);
        setWebinar(data);

        // Register if not already
        try { await webinarAPI.register(webinarId); } catch {}

        // Load message history
        const { data: msgs } = await webinarAPI.listMessages(webinarId);
        setMessages(msgs);
      } catch {
        toast.error('Webinaire introuvable');
        navigate(`/admin/webinars`);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => reset();
  }, [webinarId]);

  // Timer
  useEffect(() => {
    if (!webinar) return;
    const interval = setInterval(() => {
      const start = webinar.schedule?.start_at;
      if (start) {
        const diff = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 1000));
        setElapsed(diff);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [webinar]);

  // Socket.IO connection
  useEffect(() => {
    if (!user || !webinar) return;

    const socket = io(BACKEND_URL, {
      path: '/ws/socket.io',
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;
    setSocket(socket);

    socket.on('connect', () => {
      socket.emit('webinar:join', {
        webinar_id: webinarId,
        user_id: user.id,
        user_name: `${user.first_name} ${user.last_name}`,
        user_role: user.role,
      });
    });

    // Participants update
    socket.on('webinar:participants', (data) => {
      setParticipants(data.participants || []);
    });

    // State sync
    socket.on('webinar:state', (data) => {
      setMyRole(data.your_role || 'participant');
      setRaisedHands(data.raised_hands || []);
      setScreenSharer(data.screen_sharer);
      if (data.participants) setParticipants(data.participants);
    });

    // Chat
    socket.on('webinar:chat', (msg) => addMessage(msg));
    socket.on('webinar:chat:delete', ({ message_id }) => removeMessage(message_id));
    socket.on('webinar:chat:pin', ({ message_id, is_pinned }) => pinMessage(message_id, is_pinned));

    // Media state
    socket.on('webinar:mute', ({ user_id, is_muted }) => {
      setParticipants(prev => prev.map(p => p.user_id === user_id ? { ...p, is_muted } : p));
    });
    socket.on('webinar:camera', ({ user_id, is_camera_on }) => {
      setParticipants(prev => prev.map(p => p.user_id === user_id ? { ...p, is_camera_on } : p));
    });

    // Screen share
    socket.on('webinar:screen-start', ({ user_id }) => setScreenSharer(user_id));
    socket.on('webinar:screen-stop', () => setScreenSharer(null));

    // Reactions
    socket.on('webinar:reaction', (data) => {
      const key = Date.now() + Math.random();
      addReaction({ ...data, _key: key });
      setTimeout(() => clearReaction(key), 3000);
    });

    // Hand raise
    socket.on('webinar:hand-raise', (data) => setRaisedHands(data.raised_hands || []));

    // Polls
    socket.on('webinar:poll:start', (poll) => setActivePoll(poll));
    socket.on('webinar:poll:results', (poll) => setActivePoll(poll));
    socket.on('webinar:poll:end', (poll) => setActivePoll({ ...poll, status: 'closed' }));

    // Kicked
    socket.on('webinar:kicked', ({ reason }) => {
      toast.error(`Vous avez été éjecté${reason ? ': ' + reason : ''}`);
      navigate(`/admin/webinars`);
    });

    // Mute all
    socket.on('webinar:mute-all', ({ by }) => {
      toast.info(`${by} a coupé tous les micros`);
      useWebinarStore.setState({ isMuted: true });
    });

    // WebRTC signaling
    socket.on('webinar:offer', async ({ sdp, from_user_id, from_user_name }) => {
      try {
        const pc = createPeerConnection(from_user_id, socket);
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('webinar:answer', { sdp: answer, target_user_id: from_user_id });
      } catch (e) { console.error('WebRTC offer error', e); }
    });

    socket.on('webinar:answer', async ({ sdp, from_user_id }) => {
      const pc = peerConnectionsRef.current[from_user_id];
      if (pc) {
        try { await pc.setRemoteDescription(new RTCSessionDescription(sdp)); }
        catch (e) { console.error('WebRTC answer error', e); }
      }
    });

    socket.on('webinar:ice', async ({ candidate, from_user_id }) => {
      const pc = peerConnectionsRef.current[from_user_id];
      if (pc && candidate) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); }
        catch (e) { console.error('ICE error', e); }
      }
    });

    return () => {
      socket.emit('webinar:leave');
      socket.disconnect();
      // Cleanup peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
      peerConnectionsRef.current = {};
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      screenStreamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [user, webinar?.id]);

  // Handle local media (camera/mic)
  useEffect(() => {
    const updateMedia = async () => {
      try {
        // If we don't have a stream yet but need one
        if (!localStreamRef.current && (isCameraOn || !isMuted)) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
          localStreamRef.current = stream;
          setLocalStreamState(stream);
          
          // Add tracks to existing peer connections
          Object.values(peerConnectionsRef.current).forEach(pc => {
            stream.getTracks().forEach(track => pc.addTrack(track, stream));
          });
        }
        
        // Update track enabled state
        if (localStreamRef.current) {
          localStreamRef.current.getVideoTracks().forEach(t => t.enabled = isCameraOn);
          localStreamRef.current.getAudioTracks().forEach(t => t.enabled = !isMuted);
        }
      } catch (err) {
        console.error('Media access error', err);
        toast.error("Impossible d'accéder à la caméra ou au micro");
        useWebinarStore.setState({ isCameraOn: false, isMuted: true });
      }
    };
    updateMedia();
  }, [isCameraOn, isMuted]);

  // Create WebRTC peer connection
  const createPeerConnection = useCallback((remoteUserId, socket) => {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    peerConnectionsRef.current[remoteUserId] = pc;

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        socket.emit('webinar:ice', { candidate: e.candidate, target_user_id: remoteUserId });
      }
    };

    pc.ontrack = (e) => {
      setRemoteStreams(prev => ({ ...prev, [remoteUserId]: e.streams[0] }));
    };

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    return pc;
  }, []);

  // Toggle screen sharing
  const handleToggleScreen = async () => {
    if (isScreenSharing) {
      screenStreamRef.current?.getTracks().forEach(t => t.stop());
      screenStreamRef.current = null;
      setIsScreenSharing(false);
      socketRef.current?.emit('webinar:screen-stop');
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = stream;
        setIsScreenSharing(true);
        socketRef.current?.emit('webinar:screen-start', {});

        stream.getVideoTracks()[0].onended = () => {
          setIsScreenSharing(false);
          socketRef.current?.emit('webinar:screen-stop');
        };
      } catch {
        toast.error('Partage d\'écran annulé');
      }
    }
  };

  // Leave
  const handleLeave = () => {
    navigate(`/admin/webinars`);
  };

  // End webinar (host)
  const handleEnd = async () => {
    if (!window.confirm('Terminer le webinaire pour tous les participants ?')) return;
    try {
      await webinarAPI.end(webinarId);
      toast.success('Webinaire terminé');
      navigate(`/admin/webinars`);
    } catch { toast.error('Erreur'); }
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-zinc-400 text-sm">Connexion au webinaire...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-zinc-950 flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 bg-zinc-900/90 backdrop-blur border-b border-zinc-800 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-semibold border border-red-500/20 animate-pulse">
            <Radio className="w-3 h-3" /> LIVE
          </span>
          <h1 className="text-sm font-semibold text-white truncate max-w-md">{webinar?.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-zinc-500">
            <Clock className="w-3.5 h-3.5" /> {formatTime(elapsed)}
          </span>
          <span className="flex items-center gap-1 text-xs text-zinc-500">
            <Users className="w-3.5 h-3.5" /> {participants.length}
          </span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex min-h-0">
        {/* Video area */}
        <div className="flex-1 flex flex-col min-w-0 relative">
          <VideoGrid
            participants={participants}
            localStream={localStreamState}
            remoteStreams={remoteStreams}
            currentUserId={user?.id}
            screenSharer={screenSharer}
          />

          {/* Floating reactions */}
          <AnimatePresence>
            {reactions.map(r => (
              <motion.div
                key={r._key}
                initial={{ opacity: 1, y: 0, x: Math.random() * 200 + 50 }}
                animate={{ opacity: 0, y: -200 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2.5, ease: 'easeOut' }}
                className="absolute bottom-24 text-3xl pointer-events-none"
              >
                {r.emoji}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Side panel */}
        {isPanelOpen && (
          <div className="w-80 border-l border-zinc-800 bg-zinc-900/50 flex flex-col shrink-0">
            {/* Panel tabs */}
            <div className="px-2 py-2 border-b border-zinc-800">
              <Tabs value={activePanel} onValueChange={setActivePanel}>
                <TabsList className="bg-zinc-800/50 w-full h-9">
                  <TabsTrigger value="chat" className="flex-1 text-xs gap-1 data-[state=active]:bg-zinc-700">
                    <MessageSquare className="w-3.5 h-3.5" /> Chat
                  </TabsTrigger>
                  <TabsTrigger value="qna" className="flex-1 text-xs gap-1 data-[state=active]:bg-zinc-700">
                    <HelpCircle className="w-3.5 h-3.5" /> Q&A
                  </TabsTrigger>
                  <TabsTrigger value="polls" className="flex-1 text-xs gap-1 data-[state=active]:bg-zinc-700">
                    <BarChart3 className="w-3.5 h-3.5" /> Sondages
                  </TabsTrigger>
                  <TabsTrigger value="participants" className="flex-1 text-xs gap-1 data-[state=active]:bg-zinc-700">
                    <Users className="w-3.5 h-3.5" /> {participants.length}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Panel content */}
            <div className="flex-1 min-h-0">
              {activePanel === 'chat' && <LiveChat currentUser={user} />}
              {activePanel === 'qna' && <QnAPanel currentUser={user} isHost={isHost} />}
              {activePanel === 'polls' && <LivePolls currentUser={user} isHost={isHost} />}
              {activePanel === 'participants' && <ParticipantsList currentUser={user} />}
            </div>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <MediaControls
        onLeave={handleLeave}
        onEndWebinar={handleEnd}
        onToggleScreen={handleToggleScreen}
        isScreenSharing={isScreenSharing}
        isHost={isHost}
      />
    </div>
  );
}
